import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";
import { notifyUserUpdated } from "@/lib/useCurrentUser";

const ProfileEditPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Phase 9.7.10 — in-app account deletion request modal.
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [formData, setFormData] = useState({
    artist_name: "",
    bio: "",
    city: "",
    country: "",
    show_location: true,
    genre: "",
    role: "artist",
    instagram: "",
    tiktok: "",
    youtube: "",
    spotify: "",
    apple_music: "",
    website: ""
  });
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const profilePhotoRef = useRef(null);
  const bannerRef = useRef(null);
  const navigate = useNavigate();

  // Phase 9.7.10 — auto-open delete modal when hash is #delete-account
  // or scroll to the Danger Zone for #account-danger-zone. Lets the
  // user-menu quick-link route straight into the deletion flow.
  useEffect(() => {
    if (loading) return;
    const hash = (window.location.hash || "").replace("#", "");
    if (hash === "delete-account") {
      setDeleteModalOpen(true);
    } else if (hash === "account-danger-zone") {
      const el = document.getElementById("account-danger-zone");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get(`/auth/me`);
      setUser(response.data);
      
      // Populate form with existing data
      setFormData({
        artist_name: response.data.artist_name || response.data.name || "",
        bio: response.data.bio || "",
        city: response.data.city || "",
        country: response.data.country || "",
        show_location: response.data.show_location !== false,
        genre: response.data.genre || "",
        role: response.data.role || "artist",
        instagram: response.data.instagram || "",
        tiktok: response.data.tiktok || "",
        youtube: response.data.youtube || "",
        spotify: response.data.spotify || "",
        apple_music: response.data.apple_music || "",
        website: response.data.website || ""
      });

      // Set existing images — strict via resolver. Legacy filenames are
      // not displayable across the app, so the resolver will fall back to
      // Google `picture` or the branded RGM placeholder automatically.
      const resolvedPhoto = getProfilePhotoUrl(response.data);
      // Don't show the placeholder SVG in the avatar slot (it's reserved
      // for empty state); only show real images.
      if (resolvedPhoto && !resolvedPhoto.startsWith('data:')) {
        setProfilePhotoPreview(resolvedPhoto);
      }

      if (response.data.banner_image && response.data.banner_image.startsWith('http')) {
        setBannerPreview(response.data.banner_image);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // P1 · Feb 2026 — TDZ fix. This mount effect references
  // ``checkAuth`` in both its body and its dependency array, so it
  // MUST be declared AFTER the ``const checkAuth = useCallback(...)``
  // above. The previous ordering (effect before declaration) put
  // ``checkAuth`` in the Temporal Dead Zone during the very first
  // render, throwing ``ReferenceError: Cannot access 'checkAuth'
  // before initialization`` (minified to ``k`` in production).
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type", { description: "Please upload a JPG, PNG, or WebP image." });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large", { description: "Maximum file size is 5MB." });
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setProfilePhotoPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/profile/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Cloudinary is mandatory — upload endpoint returns the full URL.
      // Use it directly (no legacy `/api/files/profiles/...` fallback).
      const newPhotoUrl = response.data.url;
      if (!newPhotoUrl || !newPhotoUrl.startsWith('http')) {
        throw new Error("Upload didn't return a valid URL. Please retry.");
      }
      setProfilePhotoPreview(newPhotoUrl);
      // Persist BOTH the URL and the cache-bust timestamp on the local user
      // object so anywhere that reads `user.profile_photo` + `profile_photo_updated_at`
      // (header avatar, cards, comments, etc.) re-renders immediately after
      // upload without a hard refresh. Use the SERVER's timestamp to keep
      // the cache-bust value consistent across tabs/devices.
      setUser(prev => ({
        ...prev,
        profile_photo: newPhotoUrl,
        profile_photo_updated_at:
          response.data.profile_photo_updated_at || new Date().toISOString(),
      }));
      // Phase 21.3a5 — Broadcast the update so SharedNavigation +
      // every other useCurrentUser instance re-fetches /auth/me and
      // shows the new photo immediately (no manual refresh required).
      notifyUserUpdated();
      toast.success("Profile photo uploaded!");
    } catch (error) {
      console.error("Photo upload failed:", error);
      toast.error("Failed to upload photo", { description: error.response?.data?.detail || "Please try again" });
      // Revert preview on error using the strict resolver (handles Cloudinary
      // URLs + Google `picture` fallback + branded R placeholder).
      setProfilePhotoPreview(getProfilePhotoUrl(user) || null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBannerChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type", { description: "Please upload a JPG, PNG, or WebP image." });
      return;
    }

    // Validate file size (max 10MB for banners)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large", { description: "Maximum file size is 10MB." });
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setBannerPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/profile/upload-banner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newBannerUrl = response.data.url;
      if (!newBannerUrl || !newBannerUrl.startsWith('http')) {
        // Cloudinary mandatory — bail loudly so the user knows the upload
        // didn't actually persist (was the old silent fallback to a
        // /api/files/banners/{filename} path that wouldn't survive a redeploy).
        throw new Error("Upload didn't return a valid URL. Please retry.");
      }
      setBannerPreview(newBannerUrl);
      // Phase 9.6 Batch 3 — persist BOTH url + updated_at locally so
      // getBannerUrl() cache-busts correctly on the same session. Matches
      // the photo flow above for consistency.
      setUser(prev => ({
        ...prev,
        banner_image: newBannerUrl,
        banner_image_updated_at:
          response.data.banner_image_updated_at || new Date().toISOString(),
      }));
      notifyUserUpdated();
      toast.success("Banner image uploaded!");
    } catch (error) {
      console.error("Banner upload failed:", error);
      toast.error("Failed to upload banner", { description: error.response?.data?.detail || "Please try again" });
      // Revert preview on error
      if (user.banner_image) {
        // Check if it's already a full URL (Cloudinary) or local filename
        const revertUrl = user.banner_image.startsWith('http') 
          ? user.banner_image 
          : `${BACKEND_URL}/api/files/banners/${user.banner_image}`;
        setBannerPreview(revertUrl);
      } else {
        setBannerPreview(null);
      }
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.patch(
        `/profile/update?user_id=${user.user_id}`,
        formData
      );

      toast.success("Profile updated successfully!");
      notifyUserUpdated();
      
      // Redirect to profile page
      navigate(`/artist/${user.user_id}`);
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error("Failed to update profile", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0f' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0f' }}>
      {/* Header */}
      <div className="border-b border-gray-800 bg-black/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-2">
              {/* Phase 21.3a5 — clear back to previous page (Media Control navigation fix) */}
              <Button
                onClick={() => navigate(-1)}
                variant="outline"
                className="text-white border-gray-700 hover:bg-gray-800"
                data-testid="profile-edit-back-btn"
              >
                ← Back
              </Button>
              <h1 className="text-2xl font-bold text-white" data-testid="profile-edit-title">
                Edit Profile
              </h1>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate(`/artist/${user?.user_id}`)}
                variant="outline"
                className="text-white border-gray-700 hover:bg-gray-800"
              >
                View Profile
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="text-white border-gray-700 hover:bg-gray-800"
              >
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Banner & Profile Photo Preview */}
        <Card className="mb-8 overflow-hidden bg-gradient-to-br from-purple-900/30 to-black border-purple-500/30">
          {/* Banner Section */}
          <div 
            className="relative h-48 bg-gradient-to-r from-purple-900/50 to-pink-900/50 cursor-pointer group"
            onClick={() => bannerRef.current?.click()}
            data-testid="banner-upload-area"
          >
            {bannerPreview ? (
              <img 
                src={bannerPreview} 
                alt="Banner" 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Click to upload banner (1500x500 recommended)</p>
                </div>
              </div>
            )}
            
            {/* Upload overlay */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              {uploadingBanner ? (
                <div className="flex items-center gap-2 text-white">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Uploading...</span>
                </div>
              ) : (
                <div className="text-white text-center">
                  <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm">Change Banner</p>
                </div>
              )}
            </div>
            
            <input
              ref={bannerRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleBannerChange}
              className="hidden"
              data-testid="banner-input"
            />
          </div>

          {/* Profile Photo & Info */}
          <div className="p-6 -mt-16 relative">
            <div className="flex items-end gap-6">
              {/* Profile Photo */}
              <div 
                className="relative cursor-pointer group"
                onClick={() => profilePhotoRef.current?.click()}
                data-testid="profile-photo-upload-area"
              >
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-purple-500/50 bg-gray-900">
                  <img
                    src={profilePhotoPreview || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop'}
                    alt={formData.artist_name || user?.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop'; }}
                  />
                </div>
                
                {/* Upload overlay */}
                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  {uploadingPhoto ? (
                    <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </div>
                
                <input
                  ref={profilePhotoRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleProfilePhotoChange}
                  className="hidden"
                  data-testid="profile-photo-input"
                />
              </div>

              <div className="pb-2">
                <h2 className="text-2xl font-bold text-white">
                  {formData.artist_name || user?.name || 'Your Name'}
                </h2>
                <p className="text-gray-400 text-sm">
                  Click on photo or banner to upload new images
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Edit Form */}
        <Card className="p-8" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-white border-b border-gray-800 pb-2">Basic Information</h3>
              
              <div>
                <Label htmlFor="artist_name" className="text-gray-300">Stage/Artist Name</Label>
                <Input
                  id="artist_name"
                  value={formData.artist_name}
                  onChange={(e) => handleInputChange('artist_name', e.target.value)}
                  placeholder="Your stage name"
                  className="mt-1 bg-black/50 border-gray-700 text-white"
                  data-testid="input-artist-name"
                />
                <p className="text-xs text-gray-500 mt-1">This is how you'll appear on RGM Studio</p>
              </div>

              <div>
                <Label htmlFor="bio" className="text-gray-300">Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows={4}
                  className="mt-1 bg-black/50 border-gray-700 text-white"
                  placeholder="Tell your story... What inspires your music? Where are you headed?"
                  data-testid="input-bio"
                />
              </div>

              <div>
                <Label htmlFor="genre" className="text-gray-300">Primary Genre</Label>
                <Input
                  id="genre"
                  value={formData.genre}
                  onChange={(e) => handleInputChange('genre', e.target.value)}
                  placeholder="Hip Hop, R&B, Afrobeats, etc."
                  className="mt-1 bg-black/50 border-gray-700 text-white"
                  data-testid="input-genre"
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-3 block">I am a...</Label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: 'artist', label: 'Artist', icon: '🎤', desc: 'I create and perform music' },
                    { value: 'producer', label: 'Producer', icon: '🎧', desc: 'I produce beats and instrumentals' },
                    { value: 'artist_producer', label: 'Artist + Producer', icon: '🎵', desc: 'I do both' }
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleInputChange('role', option.value)}
                      className={`flex-1 min-w-[140px] p-4 rounded-xl border-2 transition-all ${
                        formData.role === option.value
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-gray-700 bg-black/30 hover:border-gray-600'
                      }`}
                      data-testid={`role-${option.value}`}
                    >
                      <span className="text-2xl block mb-1">{option.icon}</span>
                      <span className="font-medium text-white block">{option.label}</span>
                      <span className="text-xs text-gray-500">{option.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-white border-b border-gray-800 pb-2">Location</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="city" className="text-gray-300">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="Los Angeles"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label htmlFor="country" className="text-gray-300">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="USA"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                    data-testid="input-country"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-black/30 rounded-lg border border-gray-800">
                <div>
                  <Label htmlFor="show_location" className="text-gray-300">Show location on profile</Label>
                  <p className="text-xs text-gray-500 mt-0.5">Make your location visible to others</p>
                </div>
                <Switch
                  id="show_location"
                  checked={formData.show_location}
                  onCheckedChange={(checked) => handleInputChange('show_location', checked)}
                  data-testid="switch-show-location"
                />
              </div>
            </div>

            {/* Social Links Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-white border-b border-gray-800 pb-2">Social Media & Links</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="instagram" className="text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Instagram
                  </Label>
                  <Input
                    id="instagram"
                    value={formData.instagram}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    placeholder="@yourusername or full URL"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                    data-testid="input-instagram"
                  />
                </div>

                <div>
                  <Label htmlFor="tiktok" className="text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                    TikTok
                  </Label>
                  <Input
                    id="tiktok"
                    value={formData.tiktok}
                    onChange={(e) => handleInputChange('tiktok', e.target.value)}
                    placeholder="@yourusername or full URL"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                    data-testid="input-tiktok"
                  />
                </div>

                <div>
                  <Label htmlFor="youtube" className="text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    YouTube
                  </Label>
                  <Input
                    id="youtube"
                    value={formData.youtube}
                    onChange={(e) => handleInputChange('youtube', e.target.value)}
                    placeholder="Channel URL"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                    data-testid="input-youtube"
                  />
                </div>

                <div>
                  <Label htmlFor="spotify" className="text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                    </svg>
                    Spotify
                  </Label>
                  <Input
                    id="spotify"
                    value={formData.spotify}
                    onChange={(e) => handleInputChange('spotify', e.target.value)}
                    placeholder="Artist profile URL"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                    data-testid="input-spotify"
                  />
                </div>

                <div>
                  <Label htmlFor="apple_music" className="text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.401-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.8-.364-2.22-1.163-.414-.786-.378-1.68.206-2.467.39-.525.93-.836 1.574-.97.376-.077.763-.1 1.145-.14.397-.038.79-.09 1.17-.21.193-.06.282-.17.287-.384V9.235c0-.09-.018-.136-.12-.148-.498-.06-.994-.132-1.49-.2l-3.503-.478c-.156-.02-.312-.044-.468-.06-.098-.01-.144.034-.153.13-.003.036 0 .073 0 .11v7.66c0 .376-.04.746-.18 1.1-.24.61-.674 1.04-1.285 1.28-.396.154-.81.22-1.233.24-.89.04-1.678-.243-2.193-.97-.678-.954-.596-2.313.37-3.136.514-.437 1.13-.63 1.803-.7.426-.044.856-.05 1.28-.1.306-.038.6-.11.87-.26.14-.078.2-.18.2-.34V5.02c0-.093.013-.14.112-.155l6.423-.988c.105-.017.21-.04.314-.042.078 0 .107.038.107.114v6.166z"/>
                    </svg>
                    Apple Music
                  </Label>
                  <Input
                    id="apple_music"
                    value={formData.apple_music}
                    onChange={(e) => handleInputChange('apple_music', e.target.value)}
                    placeholder="Artist profile URL"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                    data-testid="input-apple-music"
                  />
                </div>

                <div>
                  <Label htmlFor="website" className="text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
                    </svg>
                    Website
                  </Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourwebsite.com"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                    data-testid="input-website"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-800">
              <Button
                type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Save Profile"
                )}
              </Button>
              <Button
                type="button"
                onClick={() => navigate(`/artist/${user?.user_id}`)}
                variant="outline"
                className="text-white border-gray-700 hover:bg-gray-800"
              >
                Cancel
              </Button>
            </div>
          </form>

          {/* iter_217 — Notification Settings entry (previously routed
              but had no visible menu link — reachable only by typing
              the URL). Puts it right above Danger Zone so it lives in
              the standard artist Settings surface. */}
          <div
            id="notification-settings-entry"
            className="mt-8 pt-6 border-t border-purple-500/20"
            data-testid="notification-settings-entry"
          >
            <h3 className="text-lg font-bold text-purple-200 mb-1">🔔 Notifications</h3>
            <p className="text-sm text-gray-400 mb-3">
              Choose which categories reach your bell. Required
              account/billing notifications stay on so you never miss
              critical updates.
            </p>
            <Link
              to="/settings/notifications"
              data-testid="open-notification-settings"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/40 text-purple-100 font-black text-sm transition-colors"
            >
              Open Notification Settings <span aria-hidden>→</span>
            </Link>
          </div>

          {/* Phase 9.7.10 — Danger Zone: account deletion request */}
          <div
            id="account-danger-zone"
            className="mt-10 pt-6 border-t border-red-500/20 scroll-mt-24"
            data-testid="account-danger-zone"
          >
            <h3 className="text-lg font-bold text-red-300 mb-1">⚠ Danger Zone</h3>
            <p className="text-sm text-gray-400 mb-3">
              Permanently remove your account, uploaded music, and earnings history.
              This is reviewed manually by our support team — your account is{" "}
              <span className="text-white">not</span> deleted instantly.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={() => setDeleteModalOpen(true)}
                className="bg-red-600/90 hover:bg-red-600 text-white font-bold"
                data-testid="delete-my-account-btn"
              >
                Delete My Account
              </Button>
              <Link
                to="/delete-account"
                className="text-xs text-pink-300 hover:text-amber-200 underline self-center"
                data-testid="delete-account-policy-link"
              >
                Read full deletion policy →
              </Link>
            </div>
          </div>
        </Card>
      </div>

      {/* Phase 9.7.10 — Delete-account confirmation modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent data-testid="delete-account-modal" className="bg-[#0f0a14] border border-red-500/40">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Request account deletion?</DialogTitle>
            <DialogDescription className="text-gray-300 mt-2 leading-relaxed">
              This opens a pre-filled email to <span className="text-red-200 font-semibold">support@rgmstudio.org</span>.
              Our team will review and process your request within{" "}
              <span className="text-white">7 business days</span>. <br />
              <span className="text-amber-200">Your account is not deleted instantly</span> — we keep a human review step
              for security and fraud prevention. Some data may be retained temporarily
              for legal compliance.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-gray-200" data-testid="delete-account-summary">
            <p><span className="text-pink-200">Username:</span> {user?.artist_name || user?.name || "—"}</p>
            <p><span className="text-pink-200">Email:</span> {user?.email || "—"}</p>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              className="text-white border-gray-700"
              data-testid="delete-account-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                const username = (user?.artist_name || user?.name || "").trim();
                const email = (user?.email || "").trim();
                const subject = "RGM Studio Account Deletion Request";
                const body =
                  `Hi RGM Studio team,\n\n` +
                  `I'd like to delete my RGM Studio account.\n\n` +
                  `Username: ${username || "(please look up by email)"}\n` +
                  `Registered email: ${email || "(please reply to this address)"}\n\n` +
                  `I understand my account will not be deleted instantly and that processing takes up to 7 business days.\n\n` +
                  `Thanks.`;
                const url =
                  `mailto:support@rgmstudio.org?subject=${encodeURIComponent(subject)}` +
                  `&body=${encodeURIComponent(body)}`;
                window.location.href = url;
                setDeleteModalOpen(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              data-testid="delete-account-confirm"
            >
              Send Deletion Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfileEditPage;
